/**
 * @file Onglet « FX de tir » de l'ecran Skins : 20 effets cosmetiques volumineux (flash de bouche, traine, explosion) qui se superposent aux skins de
 * munitions de toutes les armes. Achat gemmes / Rubis, equipement, retrait.
 */
import type { ShotFxDef } from '@config/types';
import { Decimal } from '@shared/bignum';
import type { WalletId } from '@shared/enums';
import { Component } from '../../core/Component';
import { uiIcon } from '../../core/uiIcons';
import { h, setText, toggleClass } from '../../core/dom';
import { confirmAsk, send } from '../../core/feedback';
import { shotFxPreview } from '../../core/icons';
import { balanceOf, walletChips } from '../../core/widgets';
import type { UiContext } from '../../UiContext';

interface CardView {
  fx: ShotFxDef | null;
  root: HTMLElement;
  state: HTMLElement;
  costs: HTMLElement | null;
}

export class ShotFxPane extends Component {
  private readonly cards: CardView[] = [];

  constructor(ctx: UiContext) {
    super(ctx, 'div', 'shotfx-pane');
  }

  protected build(): void {
    const t = this.ctx.i18n;
    const grid = h('div', { class: 'aura-grid' });
    const noneState = h('div', { class: 'skin-state' });
    const none = h(
      'button',
      { class: 'skin-card aura-card', type: 'button', 'data-ui-block': true, 'data-shotfx': 'none', onclick: () => void send(this.ctx, { type: 'shotfx.equip', id: null }) },
      h('div', { class: 'aura-view' }, h('span', { class: 'aura-none' }, uiIcon('none'))),
      h('div', { class: 'skin-name' }, t.t('fx.none')),
      noneState,
    );
    this.cards.push({ fx: null, root: none, state: noneState, costs: null });
    grid.append(none);
    for (const fx of this.ctx.config.shotFx) {
      const state = h('div', { class: 'skin-state' });
      const costs = h('div', { class: 'skin-costs' }, ...walletChips(this.ctx, fx.cost));
      const root = h(
        'button',
        { class: 'skin-card aura-card', type: 'button', 'data-ui-block': true, 'data-shotfx': fx.id, onclick: () => void this.activate(fx) },
        h('div', { class: 'aura-view fw-view' }, shotFxPreview(fx.style)),
        h('div', { class: 'skin-name' }, t.name(fx.name)),
        state,
        costs,
      );
      this.cards.push({ fx, root, state, costs });
      grid.append(root);
    }
    this.el.append(h('p', { class: 'hint' }, t.t('shotfx.hint')), grid);
    this.update(0);
  }

  private async activate(fx: ShotFxDef): Promise<void> {
    const { data } = this.ctx.sim;
    if (data.shotFx.owned.includes(fx.id)) {
      void send(this.ctx, { type: 'shotfx.equip', id: fx.id });
      return;
    }
    if (fx.cost.dia !== undefined && !(await confirmAsk('skins.confirmGems'))) return;
    const r = send(this.ctx, { type: 'shotfx.buy', id: fx.id });
    if (r.ok) void send(this.ctx, { type: 'shotfx.equip', id: fx.id });
  }

  override update(_dt: number): void {
    const { data } = this.ctx.sim;
    const { i18n } = this.ctx;
    for (const c of this.cards) {
      const owned = c.fx === null || data.shotFx.owned.includes(c.fx.id);
      const equipped = c.fx === null ? data.shotFx.equipped === null : data.shotFx.equipped === c.fx.id;
      toggleClass(c.root, 'owned', owned);
      toggleClass(c.root, 'equipped', equipped);
      if (c.costs) toggleClass(c.costs, 'is-hidden', owned);
      setText(c.state, equipped ? i18n.t('common.equipped') : owned ? i18n.t('common.equip') : '');
      if (c.fx) {
        const cost = c.fx.cost;
        const affordable = (Object.keys(cost) as WalletId[]).every((id) => {
          const v = cost[id];
          return v === undefined || balanceOf(this.ctx, id).gte(Decimal.fromValue(v));
        });
        toggleClass(c.root, 'disabled', !owned && !affordable);
      }
    }
  }
}
